import { PriceListCollection, PriceListEntryList, PriceListResponse, PriceResolutionResponse, PriceTierList, } from '@aeris/contracts';
import { request } from "./client.js";
/**
 * `/api/v1/pricing` — listas, faixas e a resolução de preço.
 *
 * Todo valor atravessa esta fronteira como string, pelo motivo da ADR-0016: um
 * número JSON é um double IEEE-754, e ler `19.90` para dentro de um já perde a
 * garantia antes de qualquer código nosso ver.
 *
 * O navegador saberia resolver o preço sozinho — @aeris/pricing roda aqui
 * também — mas um preço mostrado ao cliente deve vir do mesmo lugar de onde a
 * venda vai lê-lo.
 */
/**
 * Every amount crossing this boundary is a string.
 *
 * The contracts declare them as `z.string()` for the reason ADR-0016 gives:
 * a JSON number is an IEEE-754 double, and parsing `19.90` into one loses the
 * guarantee before any of our code sees it. `formatMoney` turns the string
 * into `R$ 19,90` for a reader; `parseHumanAmount` turns what somebody typed
 * back into an exact value.
 */
export const fetchPriceLists = async (signal) => PriceListCollection.parse(await request('/api/v1/pricing/price-lists', { signal }));
export const createPriceList = async (input) => PriceListResponse.parse(await request('/api/v1/pricing/price-lists', {
    method: 'POST',
    body: input,
}));
export const updatePriceList = async (id, input) => PriceListResponse.parse(await request(`/api/v1/pricing/price-lists/${id}`, {
    method: 'PATCH',
    body: input,
}));
export const archivePriceList = async (id, version) => PriceListResponse.parse(await request(`/api/v1/pricing/price-lists/${id}/archive`, {
    method: 'POST',
    body: { version },
}));
export const fetchPriceEntries = async (priceListId, query, signal) => {
    const params = new URLSearchParams();
    if (query.search !== undefined && query.search.length > 0) {
        params.set('search', query.search);
    }
    if (query.pricedOnly === true)
        params.set('pricedOnly', 'true');
    if (query.page !== undefined)
        params.set('page', String(query.page));
    if (query.pageSize !== undefined) {
        params.set('pageSize', String(query.pageSize));
    }
    if (query.sort !== undefined)
        params.set('sort', query.sort);
    if (query.direction !== undefined)
        params.set('direction', query.direction);
    const encoded = params.toString();
    return PriceListEntryList.parse(await request(`/api/v1/pricing/price-lists/${priceListId}/items${encoded.length > 0 ? `?${encoded}` : ''}`, { signal }));
};
export const fetchPriceTiers = async (priceListId, variantId, signal) => PriceTierList.parse(await request(`/api/v1/pricing/price-lists/${priceListId}/items/${variantId}`, { signal }));
export const setPriceItem = async (priceListId, input) => PriceTierList.parse(await request(`/api/v1/pricing/price-lists/${priceListId}/items`, {
    method: 'POST',
    body: input,
}));
export const removePriceItem = async (priceListId, itemId, version) => PriceTierList.parse(await request(`/api/v1/pricing/price-lists/${priceListId}/items/${itemId}/remove`, { method: 'POST', body: { version } }));
/**
 * Asks the server what a product costs, and why.
 *
 * The browser could resolve this itself — `@aeris/pricing` runs here too — but
 * a price shown to a customer should come from the same place the sale will
 * read it from. The shared package is what guarantees the two agree; asking
 * the server is what makes the answer authoritative.
 */
export const resolvePriceFor = async (query, signal) => {
    const params = new URLSearchParams({
        variantId: query.variantId,
        quantity: query.quantity,
    });
    if (query.priceListId !== undefined) {
        params.set('priceListId', query.priceListId);
    }
    return PriceResolutionResponse.parse(await request(`/api/v1/pricing/resolve?${params.toString()}`, { signal }));
};
//# sourceMappingURL=pricing.js.map